import { FormEvent, useState } from 'react';
import { FaLinkedin, FaTiktok, FaXTwitter } from 'react-icons/fa6';
import toast from 'react-hot-toast';
import Link from '@/components/common/Link';
import { TurboLogo } from '../icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { submitNewsletterSignup } from '@/api/newsletter';

const sections = [
  {
    title: 'Services',
    links: [
      { name: 'Rapid SaaS', href: '/services/mach' },
      { name: 'AI Agents', href: '/services/nexus' },
    ],
  },
  {
    title: 'Company',
    links: [
      { name: 'About', href: '/about' },
      { name: 'Recent Work', href: '/work' },
      { name: 'Blog', href: '/blog' },
      { name: 'Contact', href: '/contact' },
    ],
  },
  {
    title: 'Resources',
    links: [
      {
        name: 'The AI Gold Rush Explained',
        href: '/blog/the-ai-gold-rush-multiplying-business-capabilities',
      },
      {
        name: 'The Market Has Already Changed',
        href: '/blog/market-shift',
      },
    ],
  },
];

const Footer4 = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) {
      toast.error('Please add an email address.');
      return;
    }

    setStatus('loading');

    try {
      await submitNewsletterSignup({ email: email.trim(), source: 'footer' });
      setStatus('success');
      setEmail('');
      toast.success('Thanks for subscribing. We just saved your spot.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to subscribe right now.';
      toast.error(message);
      setStatus('idle');
    }
  };

  return (
    <section className="w-full py-32 bg-zinc-50 dark:bg-zinc-950 border-t border-zinc-200 dark:border-zinc-800">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <footer>
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-6">
            <div className="col-span-2 flex h-full items-center justify-between md:items-start lg:col-span-3 lg:flex-col">
              <Link href="/" className="mb-8">
                <div className="w-60">
                  <TurboLogo />
                </div>
              </Link>
              <ul className="text-zinc-500 dark:text-zinc-400 flex items-center space-x-6">
                <li className="hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors">
                  <a href="https://www.linkedin.com/company/turbo-labs-inc">
                    <FaLinkedin className="size-6" />
                  </a>
                </li>
                <li className="hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors">
                  <a href="https://www.tiktok.com/@skoorbcaz">
                    <FaTiktok className="size-6" />
                  </a>
                </li>
                <li className="hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors">
                  <a href="https://x.com/turboZB">
                    <FaXTwitter className="size-6" />
                  </a>
                </li>
              </ul>
            </div>
            <Separator className="col-span-2 my-6 lg:hidden" />
            {sections.map((section, sectionIdx) => (
              <div key={sectionIdx}>
                <h3 className="mb-4 font-normal text-zinc-900 dark:text-zinc-100">{section.title}</h3>
                <ul className="text-zinc-500 dark:text-zinc-400 space-y-4">
                  {section.links.map((link, linkIdx) => (
                    <li
                      key={linkIdx}
                      className="hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors"
                    >
                      <Link href={link.href}>{link.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Separator className="my-14 lg:my-20" />
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
            <div id="footer-newsletter">
              <p className="mb-2 text-3xl font-normal lg:text-4xl text-zinc-900 dark:text-zinc-100">
                Join our newsletter
              </p>
              <p className="text-zinc-500 dark:text-zinc-400">
                Get exclusive news, features, and updates.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="flex w-full max-w-sm items-center space-x-2">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(event) => {
                  if (status === 'success') {
                    setStatus('idle');
                  }
                  setEmail(event.target.value);
                }}
                disabled={status === 'loading'}
                aria-label="Email address"
                required
              />
              <Button
                type="submit"
                disabled={status === 'loading'}
                className="min-w-[7.5rem]"
              >
                {status === 'loading' ? 'Submitting...' : status === 'success' ? 'Added' : 'Subscribe'}
              </Button>
            </form>
          </div>
          <Separator className="my-14 lg:my-20" />
          <div className="text-zinc-500 dark:text-zinc-400 flex flex-col justify-between gap-4 text-sm font-medium md:flex-row md:items-center">
            <ul className="flex gap-4">
              <li className="hover:text-primary-600 dark:hover:text-primary-400 underline transition-colors">
                <Link href="/terms">Terms and Conditions</Link>
              </li>
              <li className="hover:text-primary-600 dark:hover:text-primary-400 underline transition-colors">
                <Link href="/privacy">Privacy Policy</Link>
              </li>
            </ul>
            <p>© {new Date().getFullYear()} Turbo Labs, Inc. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </section>
  );
};

export { Footer4 };
